import React from 'react';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';
import { Button, FormControlLabel, Switch } from '@material-ui/core';

import { Surgery } from '../../../types/surgery';
import TextEditor from '../../FormEditors/TextEditor';
import usePublicProfileForm, { PublicProfileUpdate } from '../hooks/usePublicProfileForm';
import OpeningHoursEditor from './OpeningHoursEditor';

interface Props {
  surgery: Surgery;
  onSave: (update: PublicProfileUpdate) => Promise<unknown>;
}

const PublicProfileCard = ({ surgery, onSave }: Props) => {
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = usePublicProfileForm(surgery);

  return (
    <Card>
      <Title>Public profile</Title>
      <Controller
        control={control}
        name="list_on_directory"
        render={({ field }) => (
          <FormControlLabel
            label="Deploy SEO page"
            control={
              <Switch
                checked={field.value}
                onChange={(event) => field.onChange(event.target.checked)}
                inputProps={{ 'aria-label': 'Deploy SEO page' }}
              />
            }
          />
        )}
      />
      <TextEditor
        control={control}
        name="public_phone"
        id="public-phone"
        label="Public phone"
        optional
        helperText="Shown on the GP page. Leave empty to show no phone."
      />
      <TextEditor
        control={control}
        name="public_email"
        id="public-email"
        label="Public email"
        optional
        helperText="Shown on the GP page. Leave empty to show no email."
      />
      <TextEditor
        control={control}
        name="website"
        id="website"
        label="Website"
        optional
        helperText="The practice's own website. Leave empty if it has none."
      />
      <div>
        <Subtitle>Opening hours</Subtitle>
        <Controller
          control={control}
          name="hours"
          render={({ field }) => <OpeningHoursEditor value={field.value} onChange={field.onChange} />}
        />
      </div>
      <Actions>
        <Button
          variant="contained"
          color="primary"
          disabled={!isValid || isSubmitting}
          onClick={handleSubmit((update) => onSave(update))}
          aria-label="Save public profile"
        >
          Save
        </Button>
      </Actions>
    </Card>
  );
};

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 28px;
  margin-top: 20px;
`;

const Title = styled.h3`
  margin: 0;
`;

const Subtitle = styled.h4`
  margin: 0 0 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export default PublicProfileCard;
